/**
 * utils/video.js
 * The Video screen's small pieces: the form's defaults, turning the form into
 * a request and a past video back into the form, and the price.
 *
 * The defaults come from /api/videos/options, which reads them from the
 * Worker's config — one list of P-Video's inputs, not one here and one there.
 */

/** Statuses still worth asking about. */
export const ACTIVE = ['starting', 'processing', 'saving'];

export function emptyForm(o) {
  return {
    prompt: '',
    image: null,
    last_frame_image: null,
    audio: null,
    duration: o.duration.default,
    aspect_ratio: o.aspectRatios.default,
    resolution: o.resolutions.default,
    fps: o.fps.default,
    draft: o.defaults.draft,
    prompt_upsampling: o.defaults.prompt_upsampling,
    disable_safety_filter: o.defaults.disable_safety_filter,
    save_audio: o.defaults.save_audio,
    seed: '',
  };
}

const pictureBody = (v) => (!v ? null : v.media_id ? { media_id: v.media_id } : { url: v.url });

export function bodyFromForm(form, brandId) {
  let audio = null;
  if (form.audio?.key) audio = { key: form.audio.key, seconds: form.audio.seconds, name: form.audio.name };
  else if (form.audio?.url) audio = { url: form.audio.url, seconds: form.audio.seconds };

  return {
    brand_id: brandId,
    prompt: form.prompt,
    duration: form.duration,
    aspect_ratio: form.aspect_ratio,
    resolution: form.resolution,
    fps: form.fps,
    draft: form.draft,
    prompt_upsampling: form.prompt_upsampling,
    disable_safety_filter: form.disable_safety_filter,
    save_audio: form.save_audio,
    seed: form.seed === '' ? null : form.seed,
    image: pictureBody(form.image),
    last_frame_image: pictureBody(form.last_frame_image),
    audio,
  };
}

/** "Tweak it": a past video's settings, back in the form. */
export function formFromJob(job, o) {
  const p = job.params ?? {};
  const picture = (v) =>
    !v ? null : v.media_id ? { media_id: v.media_id, name: 'Library picture' } : { url: v.url, name: v.url, preview: v.url };
  const blank = emptyForm(o);

  return {
    ...blank,
    prompt: p.prompt ?? job.prompt ?? '',
    duration: p.duration ?? blank.duration,
    aspect_ratio: p.aspect_ratio ?? blank.aspect_ratio,
    resolution: p.resolution ?? blank.resolution,
    fps: p.fps ?? blank.fps,
    draft: p.draft ?? blank.draft,
    prompt_upsampling: p.prompt_upsampling ?? blank.prompt_upsampling,
    disable_safety_filter: p.disable_safety_filter ?? blank.disable_safety_filter,
    save_audio: p.save_audio ?? blank.save_audio,
    seed: p.seed ?? '',
    image: picture(p.image),
    last_frame_image: picture(p.last_frame_image),
    audio: p.audio ? { ...p.audio, name: p.audio.name ?? p.audio.url ?? 'Audio' } : null,
  };
}

/**
 * The price before pressing Run. Mirrors the Worker's own sum, which is the
 * one held against the cap.
 */
export function estimate(form, o) {
  const lengthKnown = !form.audio || !!form.audio.seconds;
  const seconds = form.audio
    ? form.audio.seconds
      ? Math.max(1, Math.ceil(form.audio.seconds))
      : o.duration.max
    : Number(form.duration) || o.duration.default;
  const perSecond = o.usdPerSecond[form.resolution]?.[form.draft ? 'draft' : 'full'] ?? 0;
  const usd = seconds * perSecond;
  return { seconds, usd, pence: usd * o.usdToPence, lengthKnown };
}

export const gbp = (pence) => `£${(Math.max(0, pence) / 100).toFixed(2)}`;

export const usd = (dollars) => `$${dollars < 0.1 ? dollars.toFixed(3) : dollars.toFixed(2)}`;

export function formatSeconds(total) {
  const s = Math.max(0, Math.round(total));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** @param {string} from - ISO time  @param {string|number} [to] - ISO time or epoch ms */
export const elapsedSeconds = (from, to = Date.now()) =>
  (new Date(to).getTime() - new Date(from).getTime()) / 1000;

/** Windows often reports no type for a .flac, so the extension decides then. */
export function audioType(file) {
  if (file.type) return file.type.toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase();
  return { mp3: 'audio/mpeg', wav: 'audio/wav', flac: 'audio/flac' }[ext] ?? '';
}

/** How long an audio file is, read by the browser. Null if it cannot say. */
export function measureAudio(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => done(null), 8000);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      clearTimeout(timer);
      done(Number.isFinite(audio.duration) ? audio.duration : null);
    };
    audio.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    audio.src = url;
  });
}
